import React from 'react';
import styled from 'styled-components';
import { IconBrandGuidelines, IconCollection, IconImage, IconSpinner4 } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import { SEARCH_MIN_CHARS } from '../constants';

import { useSpotlightCapabilities } from '../hooks/useSpotlightCapabilities';
import { ResultsCategory } from './constants';
import { CategoryChip } from './CategoryChip';
import {
	CategoryDisabledReason,
	getCategoryChipLabel,
	getCategoryDisabledReason,
	TranslationKey,
} from './Results/utils';
import { useSearchMode, useSearch, useSearchScope } from '../SpotlightState';

type CategoryChipDefinition = {
	key: ResultsCategory;
	Icon: React.ComponentType;
	shouldDisplay: boolean;
	isLoading: boolean;
	idleKey: TranslationKey;
	countKey: TranslationKey;
	disabledTooltipKeys: Partial<Record<CategoryDisabledReason, TranslationKey>>;
	totalResults: number;
};

export function CategoryChips() {
	const { selectedResultsCategory, setSelectedResultsCategory, selectedSearchOptions } =
		useSearchScope();
	const { searchTerm, isLoadingResults, searchResults } = useSearch();
	const { isDropdownSearchDisabled } = useSearchMode();
	const { translate } = useTranslate();
	const { canShowBrandguidelines } = useSpotlightCapabilities();

	const hasNoCountsToShow = searchTerm.length < SEARCH_MIN_CHARS || isDropdownSearchDisabled;

	// Selected search options scope the fetch to assets only (see Spotlight.tsx's
	// spotlightPromises gating), so the other categories' chips shouldn't spin for a request
	// that never went out.
	const isLoadingNonAssetResults = isLoadingResults && selectedSearchOptions.length === 0;

	const RESULT_CATEGORIES: CategoryChipDefinition[] = [
		{
			key: ResultsCategory.Assets,
			Icon: IconImage,
			shouldDisplay: true,
			isLoading: isLoadingResults,
			idleKey: 'spotlight.dropdown.assets',
			countKey: 'spotlight.dropdown.assets.count',
			disabledTooltipKeys: { noMatch: 'spotlight.dropdown.assets.noMatch' },
			totalResults: searchResults.assets.total ?? 0,
		},
		{
			key: ResultsCategory.Collections,
			Icon: IconCollection,
			shouldDisplay: true,
			isLoading: isLoadingNonAssetResults,
			idleKey: 'spotlight.dropdown.collections',
			countKey: 'spotlight.dropdown.collections.count',
			disabledTooltipKeys: {
				noMatch: 'spotlight.dropdown.collections.noMatch',
				notSearchable: 'spotlight.dropdown.collections.notSearchableWithSearchOptions',
			},
			totalResults: searchResults.collections.total ?? 0,
		},
		{
			key: ResultsCategory.Guidelines,
			Icon: IconBrandGuidelines,
			shouldDisplay: canShowBrandguidelines,
			isLoading: isLoadingNonAssetResults,
			idleKey: 'spotlight.dropdown.guidelines.only',
			countKey: 'spotlight.dropdown.guidelines.only.count',
			disabledTooltipKeys: {
				noMatch: 'spotlight.dropdown.guidelines.noMatch',
				notSearchable: 'spotlight.dropdown.guidelines.notSearchableWithSearchOptions',
			},
			totalResults: searchResults.guidelines.total ?? 0,
		},
	];

	const selectCategory = (key: ResultsCategory) => {
		setSelectedResultsCategory((current) => (current === key ? current : key));
	};

	// Icons are the idle/default-state affordance only — once a search is in flight the
	// spinner takes their place, and once results resolve the icon slot is empty entirely.
	const getChipIcon = (Icon: React.ComponentType, isLoading: boolean) => {
		if (hasNoCountsToShow) {
			return <Icon />;
		}

		if (isLoading) {
			return <StyledSpinner size="16" />;
		}

		return null;
	};

	return (
		<StyledWrapper>
			{RESULT_CATEGORIES.filter(({ shouldDisplay }) => shouldDisplay).map((category) => {
				// Only judged once this chip's own results are in, so a search never flashes it disabled.
				const disabledReason =
					hasNoCountsToShow || category.isLoading
						? null
						: getCategoryDisabledReason(category.key, {
								searchResults,
								selectedSearchOptions,
						  });
				const isDisabled = hasNoCountsToShow || disabledReason !== null;
				const tooltipKey = disabledReason && category.disabledTooltipKeys[disabledReason];

				return (
					<CategoryChip
						key={category.key}
						isDisabled={isDisabled}
						isSelected={selectedResultsCategory === category.key}
						icon={getChipIcon(category.Icon, category.isLoading)}
						tooltip={tooltipKey ? translate(tooltipKey) : undefined}
						onClick={isDisabled ? undefined : () => selectCategory(category.key)}
					>
						{getCategoryChipLabel({
							...category,
							totalResults:
								disabledReason === 'notSearchable' ? 0 : category.totalResults,
							translate,
							hasNoCountsToShow,
							isLoadingResults: category.isLoading,
						})}
					</CategoryChip>
				);
			})}
		</StyledWrapper>
	);
}

const StyledWrapper = styled.div`
	display: flex;
	align-items: center;
	gap: 10px;
`;

const StyledSpinner = styled(IconSpinner4)`
	@keyframes spin {
		from {
			transform: rotate(0deg);
		}
		to {
			transform: rotate(360deg);
		}
	}
	animation: spin 2s linear infinite;
`;
